import type { BeanDraft, BeanPageExtractor, PageLoader } from "./beanInfo";
import { metaTags } from "./html";

/**
 * Layer 1: Open Graph tags. Present on nearly every roaster site.
 *  og:title → name (a trailing " | Roaster" / " – Roaster" / " - Roaster" suffix is removed when it equals og:site_name)
 *  og:site_name → roaster, og:image → label image URL, og:url → canonical source URL,
 *  og:description / meta description → description text.
 */
export class OpenGraphExtractor implements BeanPageExtractor {
  readonly name = "open-graph";

  async extract(_url: string, html: string, _loader: PageLoader): Promise<Partial<BeanDraft> | null> {
    const og: Record<string, string> = {};
    let metaDescription: string | null = null;
    for (const tag of metaTags(html)) {
      const key = (tag["property"] ?? tag["name"] ?? "").toLowerCase();
      const content = tag["content"];
      if (!content) continue;
      if (key.startsWith("og:") && !(key in og)) og[key] = content.trim();
      if (key === "description" && metaDescription === null) metaDescription = content.trim();
    }
    if (Object.keys(og).length === 0 && metaDescription === null) return null;

    const site = og["og:site_name"] ?? null;
    const title = og["og:title"] ? stripSiteSuffix(og["og:title"], site) : null;
    return {
      name: title,
      roaster: site,
      labelImageUrl: og["og:image"] ?? null,
      sourceUrl: og["og:url"] ?? null,
      description: og["og:description"] ?? metaDescription,
    };
  }
}

export function stripSiteSuffix(title: string, site: string | null): string {
  const t = title.trim();
  if (!site) return t;
  // Greedy first group: split at the LAST separator so "A - B – Site" keeps "A - B".
  const m = /^(.*)\s+[|–—-]\s+(.+?)$/.exec(t);
  if (m && (m[2] as string).trim().toLowerCase() === site.trim().toLowerCase()) return (m[1] as string).trim();
  return t;
}
